import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { PendingConfirmationsProvider } from "./contexts/PendingConfirmationsContext";
import { AuthCallback } from "./components/AuthCallback";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AppLayout } from "./components/layout/AppLayout";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import LoadsPage from "./pages/LoadsPage";
import LoadDetailPage from "./pages/LoadDetailPage";
import TruckProfilesPage from "./pages/TruckProfilesPage";
import GPSTrackingPage from "./pages/GPSTrackingPage";
import DocumentsPage from "./pages/DocumentsPage";
import RoutesPage from "./pages/RoutesPage";
import FinancialsPage from "./pages/FinancialsPage";
import SettingsPage from "./pages/SettingsPage";
import BrokersPage from "./pages/BrokersPage";
import CarriersPage from "./pages/CarriersPage";
import StagedLoadsPage from "./pages/StagedLoadsPage";
import HOSPage from "./pages/HOSPage";
import ReceivablesPage from "./pages/ReceivablesPage";
import FleetVisibilityPage from "./pages/FleetVisibilityPage";
import RateConsPage from "./pages/RateConsPage";
import AISettingsPage from "./pages/AISettingsPage";
import AdminDebugPage from "./pages/AdminDebugPage";
import OperationsPage from "./pages/OperationsPage";
import OfficeDeskPage from "./pages/OfficeDeskPage";

// Router component
function AppRouter() {
    const location = useLocation();
    
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <DashboardPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/loads"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <LoadsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/loads/:loadId"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <LoadDetailPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/operations"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <OperationsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/trucks"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <TruckProfilesPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/tracking"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <GPSTrackingPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/documents"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <DocumentsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/routes"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <RoutesPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/financials"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <FinancialsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/office-desk"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <OfficeDeskPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/office-desk/:loadId"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <OfficeDeskPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <SettingsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/brokers"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <BrokersPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/carriers"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <CarriersPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/staged-loads"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <StagedLoadsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/hos"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <HOSPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/receivables"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <ReceivablesPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/command-center"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <FleetVisibilityPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/rate-cons"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <RateConsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ai-settings"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <AISettingsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/debug"
                element={<AdminDebugPage />}
            />
            <Route
                path="*"
                element={
                    <div style={{ padding: 40, backgroundColor: '#0000ff', color: '#fff' }}>
                        <h1>404 - Route Not Found</h1>
                    </div>
                }
            />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <PendingConfirmationsProvider>
                        <AppRouter />
                        <Toaster 
                            position="top-right" 
                            theme="dark"
                            toastOptions={{
                                style: {
                                    background: '#18181b',
                                    border: '1px solid #27272a',
                                    color: '#fafafa'
                                }
                            }}
                        />
                    </PendingConfirmationsProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
